interface UndoItem {
  actionId: string;
  restoreFn: () => void;
  expiresAt: number;
  timeoutId: ReturnType<typeof setTimeout>;
}

const undoItems = new Map<string, UndoItem>();

/**
 * Register an undo action. Stores a restore function that can be
 * triggered until the timeout expires.
 */
export function registerUndo(
  actionId: string,
  restoreFn: () => void,
  timeoutMs: number = 5000
) {
  // Cancel any existing undo for the same action
  cancelUndo(actionId, false);

  const expiresAt = Date.now() + timeoutMs;
  const timeoutId = setTimeout(() => {
    undoItems.delete(actionId);
  }, timeoutMs);

  undoItems.set(actionId, { actionId, restoreFn, expiresAt, timeoutId });
}

/**
 * Cancel a pending undo and execute its restore function.
 */
export function cancelUndo(actionId: string, runRestore: boolean = true) {
  const item = undoItems.get(actionId);
  if (!item) return;

  clearTimeout(item.timeoutId);
  undoItems.delete(actionId);

  if (runRestore) {
    try {
      item.restoreFn();
    } catch (err) {
      console.error('Undo restore failed', err);
    }
  }
}

/**
 * Clears all pending undo actions without executing their restore
 * functions. Useful on navigation where actions become permanent.
 */
export function clearAllUndos() {
  undoItems.forEach((item) => clearTimeout(item.timeoutId));
  undoItems.clear();
}
